import { useQuery } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { api } from "../../lib/api";
import { RouteError, RouteLoading } from "../shared/PageState";
import { labelFromCode, statusTone } from "../shared/format";
import { platformRoleLabels } from "./types";
import "./platformUsers.css";

export function RequirePlatformUserAdmin() {
  const dashboard = useQuery({ queryKey: ["tech-ops-dashboard"], queryFn: api.getDashboard });
  if (dashboard.isError) return <RouteError title="Platform access unavailable" detail="Try loading your access details again." onRetry={() => void dashboard.refetch()} />;
  if (!dashboard.data) return <RouteLoading />;
  return dashboard.data.platform_user.roles.includes("TECH_ADMIN") ? <Outlet /> : <Navigate to="/" replace />;
}

export default function PlatformUsersPage() {
  const query = useQuery({ queryKey: ["platform-users"], queryFn: api.listPlatformUsers });
  const location = useLocation();
  const success = (location.state as { success?: string } | null)?.success;

  return <div className="pa-page-stack pa-platform-users">
    <div className="pa-title-row">
      <div><h1>Platform users</h1><p>People with access to the Coprint operations dashboard.</p></div>
      <Link className="pa-primary-button" to="/platform-users/new"><UserPlus size={17} aria-hidden="true" /> Add platform user</Link>
    </div>
    {success ? <div className="pa-form-success" role="status">{success}</div> : null}
    {query.isLoading ? <p role="status">Loading platform users…</p> : query.isError ? (
      <RouteError title="Could not load platform users" detail={query.error.message} onRetry={() => void query.refetch()} />
    ) : query.data?.length ? (
      <section className="pa-panel" aria-label="Platform users">
        <div className="pa-panel-header"><h2>Platform access</h2><span>{query.data.length} {query.data.length === 1 ? "user" : "users"}</span></div>
        <ul className="pa-platform-user-list">
          {query.data.map(user => {
            const status = user.user_status !== "ACTIVE" ? user.user_status : user.status;
            return <li key={user.platform_user_id}>
              <div className="pa-platform-user-identity">
                <Link to={`/users/${encodeURIComponent(user.user_id)}`}>{user.name || user.mobile_number}</Link>
                <span>{user.mobile_number}</span>
                {user.email ? <span>{user.email}</span> : null}
              </div>
              <div className="pa-platform-user-access">
                <span>{user.roles.length ? user.roles.map(role => platformRoleLabels[role]).join(", ") : "No platform role"}</span>
                <span className={`pa-badge ${statusTone(status)}`}>{labelFromCode(status)}</span>
              </div>
            </li>;
          })}
        </ul>
      </section>
    ) : <section className="pa-panel pa-detail-padding"><h2>No platform users yet</h2><p>Add a platform user to give them access to the operations dashboard.</p></section>}
  </div>;
}
